import React, { createContext, FC, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Stack, Typography, Chip } from '@mui/material';
import { Calendar, Users } from 'react-feather';

import { useGuestSpaceFavorites } from '../hooks/useGuestSpaceFavorites';
import { useAuth } from '../contexts/AuthContext';
import { PVBlack, PVGray, PVWhite } from '@pv/common/colors';
import { VenueLocationDetailsLink } from './VenueLocationDetailsLink';
import { useFilteredSearchResults } from '../pages/Search/providers/FilteredSearchResultsProvider';
import { priceCategoryLabel } from '@pv/common/utils';
import { PreviewImage } from './PreviewImage';
import { FavoriteButton } from './FavoriteButton';
import { VenueRating } from './VenueRating';

/*
 * Types.
 */

export interface PreviewableSpace {
  images: {
    url: string;
  }[];
  seated?: number;
  standing?: number;
  pathname: string;
  name: string;
  slug: string;
  venue: {
    liveAvailability: boolean;
    images?: {
      url: string;
    }[];
    profileBanner?: string;
    name: string;
    slug: string;
    priceCategory: string;
    address?: {
      city: string;
      state: string;
    };
    googlePlaceInfo?: {
      rating?: number;
      reviewCount?: number;
      mapsUrl?: string;
    };
  };
}

interface SpacePreviewContextType {
  space: PreviewableSpace;
}

const SpacePreviewContext = createContext<SpacePreviewContextType>({
  space: {
    images: [],
    seated: 0,
    standing: 0,
    pathname: '',
    name: '',
    slug: '',
    venue: {
      liveAvailability: false,
      images: [],
      profileBanner: '',
      name: '',
      slug: '',
      priceCategory: '',
      address: {
        city: '',
        state: '',
      },
      googlePlaceInfo: {
        rating: undefined,
        reviewCount: undefined,
        mapsUrl: undefined,
      },
    },
  },
});

const useSpacePreview = () => {
  return useContext(SpacePreviewContext);
};

interface SpacePreviewProps {
  space: PreviewableSpace;
}

/*
 * Components.
 */

const Badge = () => {
  const { space } = useSpacePreview();
  const badgeStyles = {
    color: PVBlack,
    fontWeight: 'medium',
    top: 8,
    left: 8,
    position: 'absolute',
  };

  if (space.venue.liveAvailability) {
    return (
      <Chip
        sx={{
          ...badgeStyles,
          backgroundColor: PVWhite,
        }}
        label="Live Availability"
        icon={<Calendar size={16} style={{ marginLeft: '10px' }} />}
      />
    );
  }

  return null;
};

export const SpacePreview: FC<SpacePreviewProps> = ({
  space,
}: SpacePreviewProps) => {
  const firstVenueImage = space.venue?.images?.[0];
  const imgPath =
    space.images[0]?.url ||
    space.venue?.profileBanner ||
    firstVenueImage?.url ||
    '/default_banner.jpg';
  const { favoriteSpaces, toggleFavorite } = useGuestSpaceFavorites();
  const { guest, setSignupDialogOpen } = useAuth();
  const isFavorited = favoriteSpaces.some(
    (favoriteSpace) => favoriteSpace.slug === space.slug,
  );

  const venueAddress = buildVenueAddress(
    space.venue.address || {
      city: '',
      state: '',
    },
  );
  const { setHoveredSearchResult } = useFilteredSearchResults();

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!guest) {
      setSignupDialogOpen(true);
      return;
    }

    await toggleFavorite(space.slug, isFavorited);
  };

  const buildSpacesSeatedStandingText = (
    seated?: number,
    standing?: number,
  ) => {
    if (!seated && !standing) {
      return '';
    } else if (seated && !standing) {
      return `${seated} seated`;
    } else if (!seated && standing) {
      return `${standing} standing`;
    } else if (seated && standing) {
      return `${seated} seated, ${standing} standing`;
    }
  };

  return (
    <SpacePreviewContext.Provider value={{ space }}>
      <Stack
        direction="column"
        onMouseEnter={() =>
          setHoveredSearchResult({ id: space.venue.slug || '' })
        }
        onMouseLeave={() => setHoveredSearchResult(null)}
      >
        <Link
          to={space.pathname}
          target={'_blank'}
          style={{ flex: 1, display: 'inline-block', position: 'relative' }}
        >
          <PreviewImage alt={space.name} src={imgPath} />
          <Badge />
          <div
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              zIndex: 10,
            }}
          >
            <FavoriteButton
              isFavorited={isFavorited}
              onClick={handleToggleFavorite}
            />
          </div>
        </Link>
        <Stack
          direction="column"
          padding="8px 0px"
          alignItems="flex-start"
          gap="2px"
          sx={{
            minHeight: '92px',
          }}
        >
          <Stack direction="row" gap="8px" alignItems="center" justifyContent="space-between" width="100%">
            <Typography
              variant="h3"
              sx={{
                fontSize: '16px',
                lineHeight: '150%',
                fontWeight: 500,
              }}
            >
              {space.name}
            </Typography>
            <VenueRating
              rating={space.venue.googlePlaceInfo?.rating}
              reviewCount={space.venue.googlePlaceInfo?.reviewCount}
            />
          </Stack>

          <VenueLocationDetailsLink
            venueName={space.venue.name}
            venueSlug={space.venue.slug}
            venueAddress={venueAddress}
            venueNameColor={PVBlack}
            venueAddressColor="#00000099"
          />

          <Stack direction="row" gap="8px" alignItems="center">
            <Typography variant="body2" color={PVGray}>
              {priceCategoryLabel(space.venue.priceCategory || '')}
            </Typography>

            {(space.seated || space.standing) && (
              <>
                <Typography variant="body2" color={PVGray}>
                  |
                </Typography>
                <Stack direction="row" gap="8px" alignItems="center">
                  <Users size={16} color={PVGray} />
                  <Typography variant="body2" color={PVGray}>
                    {buildSpacesSeatedStandingText(
                      space.seated,
                      space.standing,
                    )}
                  </Typography>
                </Stack>
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
    </SpacePreviewContext.Provider>
  );
};

/*
 * Helpers.
 */

const buildVenueAddress = (address: { city: string; state: string }) => {
  return `${address.city}, ${address.state}`;
};
